import {
  Button,
  HStack,
  SkeletonText,
  Text,
  type StackProps,
} from '@chakra-ui/react';
import { type FC, type RefObject, type ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterTags } from '$/components/core/Collection/MaterialPresentation/DataSortingRow/components/FilterTags';
import { useCollectionStore } from '$/stores/useCollectionStore';

interface Props extends StackProps {
  amount?: number | string;
  hideCount?: boolean;
  hideSorting?: boolean;
  isLoading?: boolean;
  scrollRefs?: (RefObject<HTMLDivElement> | ForwardedRef<HTMLDivElement>)[];
}

export const DataSortingRow: FC<Props> = ({
  amount,
  hideCount = false,
  isLoading = false,
  scrollRefs,
  ...props
}) => {
  const { t } = useTranslation();

  const resetFilter = useCollectionStore.useResetAppliedFilter();
  const filter = useCollectionStore.useAppliedFilterOptions();
  const brightnessFilter = useCollectionStore.useBrightnessFilter();

  const anyFilterActive =
    filter.length > 0 ||
    (brightnessFilter[0] !== 0 && brightnessFilter[1] !== 100);

  const label = anyFilterActive
    ? t('dashboard.collection.resultsAmount.withFilter', { amount })
    : t('dashboard.collection.resultsAmount.withoutFilter', { amount });

  return (
    <HStack justify='space-between' gap='6' fontSize='sm' {...props}>
      <HStack
        alignItems='center'
        align='flex-start'
        justifyContent='space-between'
        flexWrap='wrap'
        gap='6'
        w='full'
        h='39px'
      >
        <HStack minW='0'>
          {!hideCount && !isLoading && amount && (
            <Text whiteSpace='nowrap'>{label}</Text>
          )}
          {!hideCount && isLoading && (
            <SkeletonText whiteSpace='nowrap' noOfLines={2}>
              {label}
            </SkeletonText>
          )}
          <FilterTags scrollRefs={scrollRefs} />
        </HStack>

        {(filter.length > 0 ||
          !(brightnessFilter[0] == 0 && brightnessFilter[1] == 100)) && (
          <Button
            flexShrink='0'
            px='0'
            color='lighterText'
            fontSize='sm'
            onClick={() => resetFilter()}
            variant='text'
          >
            {t('dashboard.collection.resetFilter')}
          </Button>
        )}
      </HStack>
    </HStack>
  );
};
